import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Sparkles, Calendar, Mail, Check, Send } from 'lucide-react';
import MagneticButton from '../components/MagneticButton';
import { saveNewsletterEmail } from '../utils/googleSheetsIntegration';

const CTA = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    await saveNewsletterEmail(email);
    setIsSubmitting(false);
    setIsSubmitted(true);

    setTimeout(() => {
      setIsSubmitted(false);
      setEmail('');
    }, 3000);
  };

  return (
    <section
      id="cta"
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
    >
      {/* Background Glows */}
      <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-[#0066FF]/10 rounded-full blur-[200px] -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-[350px] h-[350px] bg-[#00D4AA]/10 rounded-full blur-[200px] -translate-y-1/2 pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#0066FF]/30 mb-8"
            whileHover={{ scale: 1.03 }}
          >
            <Sparkles className="w-4 h-4 text-[#0066FF]" />
            <span className="text-sm text-slate-600">Začněte hned</span>
          </motion.div>

          {/* Headline */}
          <h2 className="text-headline font-bold mb-6 text-slate-900">
            Připraveni být vidět
            <br />
            v <span className="text-gradient glow-text">AI odpovědích?</span>
          </h2>

          {/* Subtitle */}
          <p className="text-body text-lg mb-10 max-w-xl mx-auto">
            Zjistěme, jak je váš web připraven na AI vyhledávače. Konzultace je zdarma.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link to="/audit">
              <MagneticButton variant="primary">
                <Calendar className="w-5 h-5" />
                AI Visibility Audit
                <ArrowRight className="w-5 h-5" />
              </MagneticButton>
            </Link>
            <Link to="/o-nas">
              <MagneticButton variant="outline">
                <Mail className="w-5 h-5" />
                Napsat nám
              </MagneticButton>
            </Link>
          </div>

          {/* Newsletter Form */}
          <motion.div
            className="glass-card rounded-2xl p-8 max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <p className="text-slate-600 mb-6">
              Nebo se přihlaste k newsletteru s AI SEO tipy
            </p>

            {isSubmitted ? (
              <motion.div
                className="flex items-center justify-center gap-2 text-[#00D4AA]"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-10 h-10 rounded-full bg-[#00D4AA]/15 flex items-center justify-center">
                  <Check className="w-5 h-5" />
                </div>
                <span className="font-medium">Přihlášení proběhlo úspěšně!</span>
              </motion.div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3">
                <motion.input
                  type="email"
                  placeholder="Váš e-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 px-4 py-3 rounded-xl bg-white/50 border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#00D4AA]/50 focus:ring-2 focus:ring-[#00D4AA]/10 transition-all"
                  whileFocus={{ scale: 1.01 }}
                />
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#0066FF] to-[#00D4AA] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-[#00D4AA]/25"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Přihlásit
                    </>
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {['Bezplatná konzultace', 'Plán do 48 hodin', 'Bez závazků'].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#00D4AA] shadow-lg shadow-[#00D4AA]/50" />
                {item}
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTA;
